import './styles/index.scss'

import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter as Router, Link } from 'react-router-dom'
import type { CncFeatureFlags } from '@which/core-navigation-component'
import { Footer, getCncScripts, Header } from '@which/core-navigation-component'
import { getClientSideInstance } from '@which/experiments'
import { SSRProvider as ReactAriaSSRIdProvider } from '@which/seatbelt'
import { getApiEnv } from '@which/shared'
import { ApolloProvider } from '@apollo/client'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { loadableReady } from '@loadable/component'

import { App } from './App'
import * as serviceWorker from './serviceWorker'
import { isLocal } from './shared'
import { apolloClient } from './shared/apollo-client'
import { BrowserBackStackProvider } from './shared/BrowserBackStackProvider'
import { ExperimentProviderWrapper } from './shared/components/ExperimentProviderWrapper'
import { HoneycombObservability } from './shared/components/Honeycomb/Honeycomb'
import { PreviewIndicator } from './shared/components/PreviewIndicator/PreviewIndicator'
import { ReactRouterProvider } from './shared/contexts/ReactRouterContext'
import { HeadersProvider } from './shared/HeadersContext'
import { checkRoute } from './shared/utils/check-route'
import { getQueryString } from './shared/utils/get-query-string'
import { getIsHomepage, getSimplifiedNav, getUserLoggedIn } from './shared/utils/header-context'

const previewMode = getQueryString('preview') === 'true'
const client = apolloClient({ target: 'client', preview: previewMode })
const linkContext = {
  checkRoute,
  Link,
}
const getFullUrl = () => window.location.href
const { userAgent } = window.navigator
const allowedHosts = new RegExp(
  `^(localhost|(.*).which.co.uk|${process.env.LOCAL_IP_ADDRESS})$`,
  'gi'
)
allowedHosts.test(window.location.hostname) &&
  loadableReady(async () => {
    const { nonceAttribute } = window

    const enableDevMode =
      process.env.GROWTHBOOK_DEV_MODE === 'true' || window.ENV?.enableGBDevMode === 'true'

    const gbInstance = await getClientSideInstance({
      jsInjectionNonce: nonceAttribute,
      enableDevMode,
      ...(process.env.GROWTHBOOK_CLIENT_KEY &&
        process.env.GROWTHBOOK_CLIENT_KEY !== 'REPLACE WITH Growthbook client key' && {
          clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
        }),
    })

    const isLocalEnv = isLocal()

    function Root() {
      return (
        <React.StrictMode>
          <HelmetProvider context={{}}>
            <ExperimentProviderWrapper experimentObj={gbInstance}>
              <RootContent />
            </ExperimentProviderWrapper>
          </HelmetProvider>
        </React.StrictMode>
      )
    }

    /**
     * IMPORTANT:
     * Hooks using GrowthBook context must be rendered
     * INSIDE ExperimentProviderWrapper
     */
    function RootContent() {
      const { hostname, origin, pathname, host } = window.location
      const isHomepage = getIsHomepage(pathname)
      const isPolicyAndInsightArticle = pathname.includes('/policy-and-insight/article/')
      const userLoggedIn = getUserLoggedIn(document.cookie)
      const simplifiedNav = getSimplifiedNav(pathname)
      const cncEnv = getApiEnv(hostname) === 'prod' ? 'prod' : 'dev'

      const cncFeatureFlags: CncFeatureFlags = {
        'WHC-1487-ask-which-cnc': useFeatureIsOn('WHC-1487-ask-which-cnc') ?? false,
        'whc-1482-saved-articles-feature-alert':
          useFeatureIsOn('whc-1482-saved-articles-feature-alert') ?? false,
        'WHC-1475-sticky-header':
          (useFeatureIsOn('WHC-1475-sticky-header') && !isPolicyAndInsightArticle) ?? false,
      }

      return (
        <>
          <HeadersProvider
            value={{
              getFullUrl,
              userAgent,
              correlationId: window.correlationId || '',
              cookies: window.document?.cookie || '',
            }}
          >
            <ReactRouterProvider value={linkContext}>
              <ApolloProvider client={client}>
                <Router>
                  <BrowserBackStackProvider>
                    <ReactAriaSSRIdProvider>
                      <Header
                        isHomePage={isHomepage}
                        isSimplifiedHeader={simplifiedNav}
                        isLoggedIn={userLoggedIn}
                        hasLogIn={true}
                        hasSearch={true}
                        hasSavedLink={true}
                        hostname={hostname}
                        origin={origin}
                        baseUrl={host}
                        env={cncEnv}
                        featureFlags={cncFeatureFlags}
                      />
                      <App />
                      <Footer baseUrl={host} env={cncEnv} featureFlags={cncFeatureFlags} />
                    </ReactAriaSSRIdProvider>
                  </BrowserBackStackProvider>
                </Router>
              </ApolloProvider>
            </ReactRouterProvider>
          </HeadersProvider>
          <PreviewIndicator preview={previewMode} />
          <HoneycombObservability />
        </>
      )
    }

    if (isLocalEnv) {
      // TODO: skipOneTrust false for now, need to figure out how to pass true locally but not fail e2e tests
      getCncScripts({ nonce: nonceAttribute, skipOneTrust: false }).inject()
    }

    const container = document.getElementById('root')!
    if (module.hot) {
      createRoot(container).render(<Root />)
    } else {
      hydrateRoot(container, <Root />)
    }
  })

/*
 * Ensure all service workers are unregistered
 */
serviceWorker.unregister()
